import React from "react";
import dayjs from "dayjs";

// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// project import
import { OutlineButton } from "components/buttons/OutlineButton";

// assets
import icon_more from "assets/images/icons/icn_table_cell_more.png";

const RecentTransactionsBlock = ({ transactions, onShowMore }) => {
  const theme = useTheme();

  const getTypeLabel = (type) => {
    if (!type) return "";
    const mapping = {
      import: "Import",
      subscribe: "Subscribe",
      monthlycharge: "Monthly Charge",
      adddevice: "Added Device",
      removedevice: "Remove Device",
      unsubscribe: "Unsubscribe",
    };
    return mapping[type.toLowerCase()] || type;
  };

  const getDescription = (tx) => {
    const type = tx.type ? tx.type.toLowerCase() : "";
    const p = tx.descriptionParams || {};

    const getParam = (key) => {
      const lKey = key.toLowerCase();
      const actualKey = Object.keys(p).find((k) => k.toLowerCase() === lKey);
      return actualKey ? p[actualKey] : undefined;
    };

    switch (type) {
      case "import":
        return (
          <Stack spacing={0}>
            <Typography variant='caption' sx={{ color: "rgba(0,0,0,0.65)" }}>
              Account:{" "}
              <Box component='span' sx={{ fontWeight: "bold" }}>
                {getParam("account") || "-"}
              </Box>
            </Typography>
            <Typography variant='caption' sx={{ color: "rgba(0,0,0,0.65)" }}>
              License Key:{" "}
              <Box component='span' sx={{ fontWeight: "bold" }}>
                {getParam("key") || "-"}
              </Box>
            </Typography>
          </Stack>
        );
      case "subscribe":
      case "monthlycharge":
      case "adddevice":
      case "removedevice":
      case "unsubscribe":
        return (
          <Stack spacing={0}>
            <Typography variant='caption' sx={{ color: "rgba(0,0,0,0.65)" }}>
              Site:{" "}
              <Box component='span' sx={{ fontWeight: "bold" }}>
                {getParam("site") || "Org Level"}
              </Box>
            </Typography>
            <Typography variant='caption' sx={{ color: "rgba(0,0,0,0.65)" }}>
              {["monthlycharge", "adddevice"].includes(type)
                ? "Devices"
                : "Device"}
              :{" "}
              <Box component='span' sx={{ fontWeight: "bold" }}>
                {getParam("devices") || getParam("device") || 0}
              </Box>
            </Typography>
            <Typography variant='caption' sx={{ color: "rgba(0,0,0,0.65)" }}>
              Days:{" "}
              <Box component='span' sx={{ fontWeight: "bold" }}>
                {getParam("days") || 0}
              </Box>
            </Typography>
            <Typography variant='caption' sx={{ color: "rgba(0,0,0,0.65)" }}>
              Service:{" "}
              <Box component='span' sx={{ fontWeight: "bold" }}>
                {getParam("service") || "-"}
              </Box>
            </Typography>
          </Stack>
        );
      default:
        return "-";
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={2}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography
            sx={{
              color: "#172664",
              fontSize: "15px",
              fontWeight: "bold",
            }}
          >
            Recent Transactions (Last 10)
          </Typography>
          <OutlineButton
            size='small'
            onClick={onShowMore}
            height='24px'
            startIcon={<img src={icon_more} alt='more' />}
            sx={{
              fontSize: "12px",
              px: 1.5,
              borderRadius: "12px",
              color: "#172664",
              borderColor: "#172664",
              "&:hover": {
                borderColor: "#23367d",
                bgcolor: "rgba(23, 38, 100, 0.04)",
              },
            }}
          >
            Show More
          </OutlineButton>
        </Stack>
        <TableContainer
          component={Box}
          sx={{ border: "none", boxShadow: "none" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: "#172664",
                    fontWeight: "bold",
                    fontSize: "14px",
                    pb: 2,
                    pl: 0,
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: "#172664",
                    fontWeight: "bold",
                    fontSize: "14px",
                    pb: 2,
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: "#172664",
                    fontWeight: "bold",
                    fontSize: "14px",
                    pb: 2,
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: "#172664",
                    fontWeight: "bold",
                    fontSize: "14px",
                    pb: 2,
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: "#172664",
                    fontWeight: "bold",
                    fontSize: "14px",
                    pb: 2,
                    textAlign: "right",
                    pr: 0,
                  }}
                >
                  Balance After
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length > 0 ? (
                transactions.map((tx, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: index % 2 === 1 ? "#F9F9F9" : "transparent",
                      "& .MuiTableCell-root": { border: "none", py: 1.5 },
                    }}
                  >
                    <TableCell sx={{ color: "rgba(0,0,0,0.65)", pl: 0 }}>
                      {dayjs.unix(tx.timestamp).format("YYYY-MM-DD")}
                    </TableCell>
                    <TableCell>{getTypeLabel(tx.type)}</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: tx.change > 0 ? "#008514" : "#373737",
                      }}
                    >
                      {tx.change > 0
                        ? `+${tx.change.toLocaleString()}`
                        : tx.change.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: "rgba(0,0,0,0.65)" }}>
                      {getDescription(tx)}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        pr: 0,
                        color: "rgba(0,0,0,0.65)",
                      }}
                    >
                      {tx.balance.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align='center'
                    sx={{ py: 4, color: "rgba(0,0,0,0.45)" }}
                  >
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
};

export default RecentTransactionsBlock;
